import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useWaitlist } from '../../../contexts/WaitlistContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { loading, addToWaitlist, setShowSuccessModal } = useWaitlist();
  const videoRef = useRef(null);
  const gestureRetryRef = useRef(null);
  const [isVideoHidden, setIsVideoHidden] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);

  // Local form state
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const handleScroll = () => {
      setParallaxY(window.scrollY * 0.3);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const attemptPlay = useCallback((video) => {
    if (!video) return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise
        .then(() => setIsVideoHidden(false))
        .catch(() => {
          // Autoplay blocked (e.g. Low Power Mode) — hide the video (via CSS, not
          // unmount) so the native centered play glyph never sits over the
          // headline, while keeping the element + ref alive for the gesture retry.
          setIsVideoHidden(true);

          if (!gestureRetryRef.current) {
            const retry = () => {
              const el = videoRef.current;
              if (!el) return;
              el.muted = true;
              el.play()
                .then(() => setIsVideoHidden(false))
                .catch(() => {});
            };
            gestureRetryRef.current = retry;
            window.addEventListener('touchstart', retry, { once: true, passive: true });
            window.addEventListener('click', retry, { once: true });
          }
        });
    }
  }, []);

  // Remove the dangling gesture-retry listeners if the component unmounts before
  // the user ever taps/clicks.
  useEffect(() => {
    return () => {
      if (gestureRetryRef.current) {
        window.removeEventListener('touchstart', gestureRetryRef.current);
        window.removeEventListener('click', gestureRetryRef.current);
      }
    };
  }, []);

  // Ensure `muted` is set as a real DOM property (React doesn't reliably emit the
  // attribute) before attempting to play, otherwise Safari rejects autoplay. The
  // video stays mounted for the lifetime of the component (hidden via CSS only)
  // so this ref callback fires once, keeping videoRef valid for gesture retries.
  const setVideoRef = useCallback((el) => {
    videoRef.current = el;
    if (!el) return;
    el.muted = true;
    attemptPlay(el);
  }, [attemptPlay]);

  const validateEmail = (email) => {
    if (!email || email?.trim() === '') {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!email || email?.trim() === '') {
      setErrors({ email: 'Email is required' });
      return;
    }

    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('📩 Submitting waitlist email:', email);
      const result = await addToWaitlist(email);
      
      console.log('📥 addToWaitlist result:', {
        success: result?.success,
        alreadyExists: result?.alreadyExists,
        emailSent: result?.emailSent,
        emailError: result?.emailError,
        message: result?.message
      });
      
      if (result?.success) {
        setEmail('');
        setErrors({});
        
        // Show success modal
        setTimeout(() => {
          console.log('✅ Opening success modal');
          setShowSuccessModal(true);
        }, 100);
        
        if (!result?.emailSent && result?.emailError) {
          console.warn('⚠️ Email confirmation delayed:', result?.emailError);
        }
        
        if (result?.alreadyExists) {
          console.log('ℹ️ Email already exists but showing success anyway');
        }
      } else {
        setErrors({ submit: result?.message || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      console.error('❌ Waitlist submission error:', error);
      setErrors({ submit: error?.message || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="hero" className="relative min-h-dvh flex items-center justify-center overflow-hidden">
      {/* Background Layer 1: Video — static, full presence. Kept mounted even when
          autoplay fails (hidden via CSS instead) so the ref stays valid for the
          one-time gesture retry below; the native play glyph is still fully
          suppressed since the element is invisible and non-interactive. */}
      <div className="absolute inset-0 pointer-events-none">
        <video
          ref={setVideoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hero-poster.jpg"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isVideoHidden ? 'opacity-0 invisible' : 'opacity-55'
          }`}
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Background Layer 2: Light gradient overlay (strengthened through the middle
          so the headline stays legible even when the video is hidden/unplayed). */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-background/40 via-background/50 to-background/70" />

      {/* Background Layer 3: Cosmic gradient tint */}
      <div className="absolute inset-0 cosmic-gradient pointer-events-none" style={{ opacity: 0.35 }}>
        {/* Floating Particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)]?.map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary/30 rounded-full animate-pulse pointer-events-none"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Radial Glow Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Foreground Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Logo */}
        <div className={`mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl scale-150 animate-pulse" />
            <Image
              src="/brand/depthme-icon-192.png"
              alt="DepthMe AI - Transform your inner critic into your inner coach"
              className="relative w-24 h-24 mx-auto object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Headline Hierarchy */}
        <div className={`space-y-6 mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
            Your mind is{' '}
            <span className="text-gradient">training you.</span>
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            If you don't train your mind, your mind will train you. DepthMe helps you replace the thoughts that hold you back — in your own voice, every day.
          </p>
        </div>

        {/* Inline Email Form - CRITICAL: Direct input without modal */}
        <div className={`space-y-6 mb-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              <div className="flex-1 w-full">
                <Input
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  error={errors?.email}
                  className="text-lg"
                  disabled={isSubmitting}
                />
              </div>
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isSubmitting}
                className="violet-glow whitespace-nowrap"
                iconName="ArrowRight"
                iconPosition="right"
              >
                {isSubmitting ? 'Securing...' : 'Secure Your Spot'}
              </Button>
            </div>
            
            {errors?.submit && (
              <div className="text-error text-sm mt-2 text-center">{errors?.submit}</div>
            )}
          </form>

          {/* Secondary Open App CTA */}
          <div className="flex items-center justify-center">
            <a
              href="https://depthme.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth border border-border/40 hover:border-primary/50 px-5 py-2.5 rounded-lg glassmorphism hover:bg-primary/5"
            >
              <Icon name="ExternalLink" size={14} />
              <span>Already have access? Open App</span>
            </a>
          </div>


          {/* Privacy Note */}
          <div className="text-center text-xs text-muted-foreground max-w-md mx-auto">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <Icon name="Shield" size={14} className="text-accent" />
              <span>Your privacy is protected</span>
            </div>
            <p className="opacity-75">
              We'll never share your email. Unsubscribe anytime.
            </p>
          </div>
        </div>

        {/* Trust Bar */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Icon name="Mic" size={16} className="text-primary" />
              <span>Guidance in your own voice</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-muted-foreground rounded-full" />
            <div className="flex items-center space-x-2">
              <Icon name="RefreshCw" size={16} className="text-accent" />
              <span>Mind reframing + repetition</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-muted-foreground rounded-full" />
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} className="text-accent" />
              <span>Zero-knowledge privacy</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <Icon name="ChevronDown" size={24} className="text-muted-foreground" />
      </div>
    </section>
  );
};

export default HeroSection;